
/* -----------------------------------------------------------------------------------------------------
  - Created: [20-02-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD] 
  - Description: This file contains the Employee Report search filter component
------------------------------------------------------------------------------------------------------- */

import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';
import { useFormik } from 'formik';
import '../../ReportsCommonStyles/CommonSearchFilterStyle.css';
import ActionButton from '../../../../Components/custom_components/CustomButtons/ActionButton/ActionButton';
import CustomTextFieldWithIcon from '../../../../Components/custom_components/custom_text_field/CustomTextFieldWithIcon';
import CustomBreadcrumbs from '../../../../Components/custom_components/CustomBreadcrumbs/CustomBreadcrumbs';
import { get_worksite_with_out_pagination } from '../../../../config/Worksite/siteApis';
import CustomSelectDropdown from '../../../../Components/custom_components/CustomSelectDropdown/CustomSelectDropdown';
import CustomDatePicker from '../../../../Components/custom_components/CustomDatePicker/CustomDatePicker';
import dayjs from 'dayjs';
import { get_equipment_with_out_pagination } from '../../../../config/Resource/equipmentApis';
import ActionButtonWithIcon from '../../../../Components/custom_components/CustomButtons/ActionButtonWithIcon/ActionBtnWithIcon';
import { Icons } from '../../../../utils/GlobalIcons';

function EquipmentReportFilter({
  tableTitle,
  searchFilter,
  on_change_value, on_page_count_update,
  onExportPDF,
  onExportExcel,
  isExportingPDF,
  isExportingExcel
}) {
  const [worksiteOptions, setWorksiteOptions] = useState([]);
  const [equipmentListing, setEquipmentListing] = useState([]);

  useEffect(() => {
    fetchEquipments();
    fetchSites();
  }, []);

  const fetchEquipments = async () => {
    try {
      const response = await get_equipment_with_out_pagination();

      if (response?.status === "success") {
        const transformedData = response?.data?.data?.map((item) => ({
          label: `${item?.EquipmentName} (${item?.EquipmentserialNo || ''})`,
          value: item?.EquipmentId,
        })) || [];

        setEquipmentListing(transformedData);

      } else {
        setEquipmentListing([]);
        formik.setFieldValue("equipment_ids", []);
      }
    } catch (error) {
      setEquipmentListing([]);
      console.error('Failed to load projects', error);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await get_worksite_with_out_pagination();
      if (response?.status === 'success') {
        const filtered = response.data.data
          .filter(item => item?.ParentWorksiteCode !== null)
          .map(item => ({ label: item.worksiteName, value: item.WorksiteID }));
        setWorksiteOptions(filtered);
      }
    } catch (error) {
      console.error('Failed to load worksites', error);
    }
  };


  const formik = useFormik({
    initialValues: {
      scheduleCode: '',
      equipmentId: '',
      worksiteId: '',
      toDate: '',
      fromDate: '',
    },
  });

  const resetFilter = () => {
    formik.resetForm();

    on_change_value(1)
    on_page_count_update(25)

    searchFilter({
      scheduleCode: '',
      equipmentId: '',
      worksiteId: '',
      toDate: '',
      fromDate: '',
    });
  };
  const breadcrumbs = ["Reports", tableTitle];
  return (
    <div className="common-search-filter-container">
      <div className="common-search-filter-sub-container">
        <div className="common-search-header">
          <div className="common-search-left">
            <div className="common-table-title">{tableTitle}</div>
            <CustomBreadcrumbs breadcrumbs={breadcrumbs} />
          </div>
          <div className="common-export-buttons-container">
            <ActionButtonWithIcon
              label="Export PDF"
              onClick={onExportPDF}
              icon={Icons.PdfIcon}
              backgroundColor="#ffffff"
              hoverColor="#fef2f2"
              labelColor="#000000"
              borderColor="#e5e7eb"
              iconColor="#dc2626"
              loading={isExportingPDF}
            />

            <ActionButtonWithIcon
              label="Export Excel"
              onClick={onExportExcel}
              icon={Icons.ExcelIcon}
              backgroundColor="#ffffff"
              hoverColor="#f0fdf4"
              labelColor="#000000"
              borderColor="#e5e7eb"
              iconColor="#16a34a"
              loading={isExportingExcel}
            />
          </div>
        </div>

        <Grid container spacing={3} alignItems="center">


          <Grid item xs={12} sm={6} md={3}>
            <CustomSelectDropdown
              name="equipmentId"
              placeholder="Select Equipment"
              options={equipmentListing}
              value={equipmentListing.find(opt => opt.value === formik.values.equipmentId) || ''}
              onChange={opt => formik.setFieldValue('equipmentId', opt?.value || '')}
              onBlur={formik.handleBlur}
              isMulti={false}
              width="250px"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <CustomSelectDropdown
              name="worksiteId"
              placeholder="Select Worksite"
              options={worksiteOptions}
              value={worksiteOptions.find(opt => opt.value === formik.values.worksiteId) || ''}
              onChange={opt => formik.setFieldValue('worksiteId', opt?.value || '')}
              onBlur={formik.handleBlur}
              isMulti={false}
              width="200px"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <CustomTextFieldWithIcon
              id="scheduleCode"
              name="scheduleCode"
              placeholder="Search by Schedule Code"
              value={formik.values.scheduleCode}
              onChange={formik.handleChange}
              width="200px"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>


            <CustomDatePicker
              name="fromDate"
              label="From Date"
              onChange={(date) => {
                formik.setFieldValue("fromDate", date);
              }}
              onBlur={formik.handleBlur}
              value={dayjs(formik.values.fromDate, "DD-MM-YYYY")}
              width="200px"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <CustomDatePicker
              name="toDate"
              label="End Date"
              onChange={(date) => {
                formik.setFieldValue("toDate", date);
              }}
              onBlur={formik.handleBlur}
              value={dayjs(formik.values.toDate, "DD-MM-YYYY")}
              minDate={dayjs(formik.values.fromDate, "DD-MM-YYYY")} // Set minimum date
              width="200px"
            />

          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <ActionButton
                  onClick={() => {
                    on_change_value(1)
                    searchFilter({ ...formik.values }); // Always trigger search, even if no values changed
                  }}
                  btnDisable={false}
                  label="Search"
                  btnType="primary"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <ActionButton
                  onClick={resetFilter}
                  label="Reset"
                  btnType="outlined"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}

export default EquipmentReportFilter;